import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import {
  MyTimesheetEntityEditor,
  MyTimesheetEntitySelector,
} from "my-timesheet/components";
import {
  costSettingsTableDef,
  costSettingsTableDefIsMobile,
} from "my-timesheet/configs/table-defs";
import {
  useMyTimesheetBooleanState,
  useMyTimesheetCostList,
  useMyTimesheetEntityButtonGroupState,
  useMyTimesheetEntityEditorDataState,
} from "my-timesheet/hooks";
import { OcCard, OcDataTable } from "oc/components";
import { initialTableData } from "oc/configs/initial-table-data";
import { useOcTableState } from "oc/hooks";

interface MyTimesheetCostSettingsProps {
  isMobile: boolean;
}

type CostRow = {
  _id: string;
  name: string;
  color?: string | null;
  value?: number | null;
  billable?: boolean | null;
  description?: string | null;
  validFrom?: Date | string | null;
  validTo?: Date | string | null;
};

type CostValues = {
  _id?: string;
  name?: string;
  color?: string | null;
  value?: number | string | null;
  billable?: boolean | null;
  description?: string | null;
  validFrom?: Date | string | null;
  validTo?: Date | string | null;
};

export const MyTimesheetCostSettings: React.FC<MyTimesheetCostSettingsProps> = ({
  isMobile,
}) => {
  const { t } = useTranslation();

  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  const { state: dialogOpenState, setState: setDialogOpenState } =
    useMyTimesheetBooleanState({ id: "showCostSettingsDialog" });

  const tableId = "myTimesheetAdminSettingsCostsTable";
  const [tableData, setTableData] = useOcTableState(tableId, {
    ...initialTableData,
    sortField: "name",
    pageSize: 5,
  });

  const entity = "OC_COST";

  const validityState = useMyTimesheetEntityButtonGroupState(entity);
  const validity: string | undefined =
    validityState === "all" ? undefined : validityState;
  const validityParam = (validity as unknown) as boolean | undefined;

  const handleTableDataChange = (updatedData: typeof tableData) => {
    setTableData(updatedData);
  };

  const { data, loading, error, createMySheetCost, updateMySheetCost } =
    useMyTimesheetCostList({
      page: tableData.page,
      pageSize: tableData.pageSize,
      sort: tableData.sortField,
      sortDirection:
        tableData.sortDirection === "asc" || tableData.sortDirection === "desc"
          ? tableData.sortDirection
          : undefined,
      searchText: tableData.searchText.length >= 3 ? tableData.searchText : "",
      searchField: tableData.searchField,
      validity: validityParam,
    });

  const {
    state: entityData,
    setState: setEntityData,
    resetState: resetEntityDataState,
  } = useMyTimesheetEntityEditorDataState();

  const handleSelectRow = (selectedRow: CostRow) => {
    setDialogOpenState(true);
    setEntityData({
      _id: selectedRow._id,
      name: selectedRow.name,
      color: selectedRow.color ?? "",
      value: (selectedRow.value ?? 0) as number,
      billable: !!selectedRow.billable,
      description: (selectedRow.description ?? "") as string,
      validFrom: (selectedRow.validFrom as any) ?? new Date(),
      validTo: (selectedRow.validTo as any) ?? null,
    });
  };

  const handleRowSelection = (row: CostRow) => {
    if (isMobile) {
      setSelectedRowId((prevSelectedId) =>
        prevSelectedId === row?._id ? null : row?._id
      );
    } else {
      handleSelectRow(row);
    }
  };

  const handleNewEntity = () => {
    setDialogOpenState(true);
    setEntityData({
      name: "",
      color: "",
      value: 0,
      billable: false,
      description: "",
      validFrom: new Date(),
      validTo: null,
    });
  };

  const handleCloseDialog = () => {
    setDialogOpenState(false);
    resetEntityDataState();
  };

  const stripNullish = <T extends Record<string, any>>(obj: T): Partial<T> =>
    Object.fromEntries(
      Object.entries(obj).filter(([, v]) => v !== undefined && v !== null)
    ) as Partial<T>;

  const handleCreateCost = async (values: CostValues) => {
    const parsedValue =
      typeof values.value === "string" ? Number.parseFloat(values.value) : values.value;

    if (values._id) {
      const variables = stripNullish({
        _id: values._id,
        name: values.name,
        color: values.color,
        billable: values.billable,
        validFrom: values.validFrom,
        validTo: values.validTo,
        value:
          parsedValue === undefined || parsedValue === null || Number.isNaN(parsedValue)
            ? undefined
            : parsedValue,
        description:
          values.description === undefined || values.description === null
            ? undefined
            : values.description === ""
              ? ""
              : values.description,
      });

      await updateMySheetCost({ variables });
    } else {
      await createMySheetCost({
        variables: {
          name: values.name ?? "",
          color: values.color ?? "",
          billable: !!values.billable,
          validFrom: values.validFrom ?? new Date(),
          validTo: values.validTo ?? null,
          value:
            parsedValue === undefined || parsedValue === null || Number.isNaN(parsedValue)
              ? 0
              : parsedValue,
          description: values.description ?? "",
        },
      });
    }

    handleCloseDialog();
  };

  const tableDef = isMobile ? costSettingsTableDefIsMobile : costSettingsTableDef;

  const filteredTableDef = {
    id: costSettingsTableDef?.id,
    columns: costSettingsTableDef?.columns?.filter(
      (column) =>
        !costSettingsTableDefIsMobile?.columns?.some(
          (mobileColumn) => mobileColumn?.id === column?.id
        )
    ),
  };

  const list = {
    rows: data?.rows,
    total: data?.total,
  };

  return (
    <>
      <MyTimesheetEntityEditor
        dialogLabel={
          entityData._id ? t("MY_TIMESHEET_EDIT_COST") : t("MY_TIMESHEET_NEW_COST")
        }
        entity={entity}
        open={dialogOpenState}
        onClose={handleCloseDialog}
        entityData={entityData}
        saveEntity={handleCreateCost}
        isMobile={isMobile}
      />
      <OcCard label={t("OC_COST")} loading={loading} error={!!error}>
        <Box height="16px" />
        <MyTimesheetEntitySelector
          entity={entity}
          onNewEntity={handleNewEntity}
          isMobile={isMobile}
        />
        <Box height="32px" />
        <OcDataTable
          data={list}
          loading={loading}
          tableDef={tableDef}
          tableData={tableData}
          setTableData={handleTableDataChange}
          onSelectRow={handleRowSelection}
          pagination
          selectedRowId={selectedRowId ?? undefined}
          filteredTableDef={filteredTableDef}
          handleMoreButton={handleSelectRow}
          localPaging={false}
          inlineEdit={false}
          matchingMyTimeitems={[]}
          tableId={tableId}
          onRowSelection={handleRowSelection}
          examinerDasboard={false}
          role="admin"
          isNotExaminer={false}
          removeUpload={() => { }}
          onEditDocument={() => { }}
        />
      </OcCard>
    </>
  );
};

export default MyTimesheetCostSettings;
